import { Box, Button, Collapse, Grid, Typography, useMediaQuery } from '@mui/material'
import { FC, useState } from 'react'

import { Delete, ExpandLess, ExpandMore } from '@mui/icons-material'

import { theme } from '../../../theme'
import { getColorForContactFollowUpType, getReadableContactFollowUpType } from '../../../../domain/Animal/AnimalDisplay'
import { DisplayChipLikeTextField } from '../../../common/DisplayChipLikeTextField'
import { useAccountsStore } from '../../../../store/AccountsStore'
import { fullName } from '../../../../domain/Account/AccountDisplay'
import { AnimalDto, ContactHostedFamilyFollowUpDto } from '../../../../interactors/gen/backendClient'
import { EditContactFollowUpModal } from './EditContactFollowUpModal'
import { animalButtonShouldBeDisabled } from '../../../../utils/animalButtonShouldBeDisabled'

interface PropsItem {
  followUp: ContactHostedFamilyFollowUpDto
  contactFollowUps: Array<ContactHostedFamilyFollowUpDto>
  setContactFollowUps: (contactFollowUps: Array<ContactHostedFamilyFollowUpDto>) => void
  animal: AnimalDto
  disabled?: boolean
}

export const ContactFollowUpItem: FC<PropsItem> = ({
  followUp,
  contactFollowUps,
  setContactFollowUps,
  animal,
  disabled = false,
}) => {
  const accountsStore = useAccountsStore()
  const [isExpanded, setExpanded] = useState(false)
  const isCompact = useMediaQuery('(max-width:899px)')

  const handleClickOnExpand = () => {
    setExpanded(!isExpanded)
  }
  const handleClickOnDelete = () => {
    setContactFollowUps(contactFollowUps.filter((aFollowUp) => followUp !== aFollowUp))
  }

  const getAccountLabelFromId = (id: string): string => {
    if (!id) {
      return ''
    }

    const account = accountsStore.withRemovedMembers.find((account) => account.id === id)
    if (!account) {
      return `${id} (Introuvable)`
    }
    let label = account ? fullName(account) : ''

    if (account.id === accountsStore.connectedAccount?.id) {
      label = `Moi (${label})`
    }

    if (account.isDeleted) {
      label = `Compte supprimé (${label})`
    }

    return label
  }

  const renderInlineField = (label: string, value: string, maxWidth?: string) => (
    <Box
      sx={{
        minWidth: 0,
        maxWidth,
        minHeight: 42,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: '#7A8197', fontSize: { xs: 10.5, sm: 11 }, fontWeight: 500, lineHeight: 1.2, mb: 0.25 }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: '#203864',
          fontSize: { xs: 13, sm: 13.5 },
          fontWeight: 400,
          lineHeight: 1.25,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={value}
      >
        {value || '-'}
      </Typography>
    </Box>
  )

  return (
    <>
      <Box
        sx={{
          py: 1,
          borderBottom: '1px solid rgba(122, 129, 151, 0.16)',
          '&:last-of-type': { borderBottom: 'none' },
        }}
      >
        {isCompact ? (
          <Grid container columnSpacing={0.5} rowSpacing={0} alignItems="center" sx={{ margin: 0, width: '100%' }}>
            <Grid item xs={3.1}>
              {renderInlineField('Date', followUp.date, '92px')}
            </Grid>
            <Grid item xs={4.9}>
              <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 42 }}>
                <DisplayChipLikeTextField
                  label=""
                  value={getReadableContactFollowUpType(followUp.type)}
                  color={getColorForContactFollowUpType(followUp.type)}
                  fullWidth
                  compact
                  borderless
                  emphasis
                  maxChipWidth={140}
                />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box display="flex" alignItems="center" sx={{ height: '100%', justifyContent: 'flex-end', gap: 0.5 }}>
                <Button
                  onClick={handleClickOnExpand}
                  sx={{
                    minWidth: 0,
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                    p: 0,
                    borderRadius: 2,
                    color: '#F3AC00',
                    backgroundColor: 'rgba(243, 172, 0, 0.08)',
                    '&:hover': { backgroundColor: 'rgba(243, 172, 0, 0.16)' },
                  }}
                >
                  {isExpanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                </Button>
                <EditContactFollowUpModal
                  followUp={followUp}
                  contactFollowUps={contactFollowUps}
                  setContactFollowUps={setContactFollowUps}
                  animal={animal}
                  disabled={disabled}
                  iconButtonSx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }}
                />
                <Button
                  sx={{
                    minWidth: 0,
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                    p: 0,
                    borderRadius: 2,
                    color: 'error.main',
                    backgroundColor: 'rgba(220, 53, 69, 0.08)',
                    '&:hover': { backgroundColor: 'rgba(220, 53, 69, 0.14)' },
                  }}
                  onClick={() => handleClickOnDelete()}
                  disabled={animalButtonShouldBeDisabled(animal) || disabled}
                >
                  <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </Button>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '180px minmax(0, 2fr) 124px',
              columnGap: 4,
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box sx={{ width: 180, flexShrink: 0, display: 'flex', alignItems: 'center', minHeight: 42 }}>
              <DisplayChipLikeTextField
                label=""
                value={getReadableContactFollowUpType(followUp.type)}
                color={getColorForContactFollowUpType(followUp.type)}
                fullWidth
                compact
                borderless
                emphasis
                maxChipWidth={140}
              />
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(120px, 0.8fr) minmax(220px, 1fr) minmax(220px, 1fr)',
                columnGap: 4,
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              {renderInlineField('Date', followUp.date)}
              {renderInlineField("Famille d'accueil", getAccountLabelFromId(followUp.hostFamilyAccountId))}
              {renderInlineField('Interlocuteur', getAccountLabelFromId(followUp.interlocutorAccountId))}
            </Box>
            <Box display="flex" alignItems="center" justifyContent="flex-end" sx={{ width: 124, gap: 0.5 }}>
              <Button
                onClick={handleClickOnExpand}
                sx={{
                  minWidth: 0,
                  width: 32,
                  height: 32,
                  p: 0,
                  borderRadius: 2,
                  color: '#F3AC00',
                  backgroundColor: 'rgba(243, 172, 0, 0.08)',
                  '&:hover': { backgroundColor: 'rgba(243, 172, 0, 0.16)' },
                }}
              >
                {isExpanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
              </Button>
              <EditContactFollowUpModal
                followUp={followUp}
                contactFollowUps={contactFollowUps}
                setContactFollowUps={setContactFollowUps}
                animal={animal}
                disabled={disabled}
                iconButtonSx={{ width: 32, height: 32 }}
              />
              <Button
                sx={{
                  minWidth: 0,
                  width: 32,
                  height: 32,
                  p: 0,
                  borderRadius: 2,
                  color: 'error.main',
                  backgroundColor: 'rgba(220, 53, 69, 0.08)',
                  '&:hover': { backgroundColor: 'rgba(220, 53, 69, 0.14)' },
                }}
                onClick={() => handleClickOnDelete()}
                disabled={animalButtonShouldBeDisabled(animal) || disabled}
              >
                <Delete sx={{ fontSize: 20 }} />
              </Button>
            </Box>
          </Box>
        )}
        <Collapse in={isExpanded}>
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            {isCompact && (
              <>
                <Grid item sm={6} xs={12}>
                  {renderInlineField("Famille d'accueil", getAccountLabelFromId(followUp.hostFamilyAccountId))}
                </Grid>
                <Grid item sm={6} xs={12}>
                  {renderInlineField('Interlocuteur', getAccountLabelFromId(followUp.interlocutorAccountId))}
                </Grid>
              </>
            )}
            <Grid item sm={6} xs={12}>
              {renderInlineField('Détails', followUp.description || '-')}
            </Grid>
            <Grid item sm={6} xs={12}>
              <Box
                display="flex"
                flexDirection={{ xs: 'row', sm: 'column' }}
                justifyContent={{ xs: 'space-between', sm: 'center' }}
                alignItems="left"
                padding={1}
                paddingTop={1.2}
                gap={0.5}
                sx={{ color: theme.palette.grey[500] }}
              >
                {followUp.created && (
                  <Typography variant="body2" fontSize={14} sx={{ p: 0, m: 0 }}>
                    Créé le {new Date(followUp.created.at).toLocaleDateString()} par{' '}
                    {getAccountLabelFromId(followUp.created.by)}
                  </Typography>
                )}
                {followUp.modified && (
                  <Typography variant="body2" fontSize={14} sx={{ p: 0, m: 0 }}>
                    Modifié le {new Date(followUp.created.at).toLocaleDateString()} par{' '}
                    {getAccountLabelFromId(followUp.created.by)}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Collapse>
      </Box>
    </>
  )
}
